import { Link } from "react-router-dom";
import { BRAND } from "@/lib/content";
import { Instagram, Facebook, MapPin, Phone, Mail } from "lucide-react";
import Reveal from "./Reveal";

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="bg-[#0a0a0a] text-white border-t border-white/10 relative overflow-hidden"
        >
            {/* Giant brand mark */}
            <div className="px-6 sm:px-10 lg:px-16 pt-24 lg:pt-32 pb-16 items-center text-center">
                
                <img
                    src="/brand/logo-mark-white.png"
                    alt="Hoofs and Hides Logo"
                    className="mx-auto mb-8 w-48 h-24 opacity-20"
                />
                <p 
                    style={{        fontFamily:
                                        "Playfair Display, ui-sans-serif, system-ui, sans-serif",
                                    letterSpacing: "0.42em",
                                    fontSize: "clamp(1.5rem, 10vw, 1.25rem)",
                                    alignContent: "center",
                                    }}>
                    HOOFS & HIDES
                </p>
                <p
                    style={{ 
                        letterSpacing: "1em",
                        fontFamily:
                            "Montserrat, ui-sans-serif, system-ui, sans-serif",
                        fontWeight: 500,
                        fontSize: "clamp(.75rem, 3vw, 0.75rem)",
                    }}>
                    
                        
                    LEATHER DIVISION
                </p>
            </div>

            <div className="px-6 sm:px-10 lg:px-16 pb-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
                <div className="md:col-span-4">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-white/40 mb-4">
                        The Atelier
                    </p>
                    <p className="text-sm text-white/70 leading-relaxed max-w-xs font-light">
                        {BRAND.tagline}
                    </p>
                    <p className="mt-6 text-xs text-white/40 font-mono-custom">
                        Est. {BRAND.founded} · Bengaluru
                    </p>
                </div>

                <div className="md:col-span-3">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-white/40 mb-4">
                        Visit
                    </p>
                    <a
                        data-testid="footer-map-link"
                        href={BRAND.mapUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="group inline-flex items-start gap-3 text-sm text-white/80 hover:text-white transition-colors"
                    >
                        <MapPin
                            size={14}
                            className="mt-1 text-white/50 group-hover:text-white transition-colors"
                        />
                        <span className="font-light leading-relaxed">
                            {BRAND.address.line1}
                            <br />
                            {BRAND.address.line2}
                            <br />
                            {BRAND.address.line3}
                        </span>
                    </a>
                    <p className="mt-6 text-xs text-white/40 font-mono-custom">
                        Coming Soon in Assaye Road · Bengaluru
                    </p>
                    <p className="mt-6 text-xs text-white/50 font-light">
                        {BRAND.hours.weekdays}
                        <br />
                        {BRAND.hours.sunday}
                    </p>
                </div>

                <div className="md:col-span-3">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-white/40 mb-4">
                        Contact
                    </p>
                    <ul className="space-y-3 text-sm">
                        <li>
                            <a
                                href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                                data-testid="footer-phone"
                                className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                            >
                                <Phone size={13} className="text-white/50" />
                                {BRAND.phone}
                            </a>
                        </li>
                        <li>
                            <a
                                href={`mailto:${BRAND.email}`}
                                data-testid="footer-email"
                                className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                            >
                                <Mail size={13} className="text-white/50" />
                                {BRAND.email}
                            </a>
                        </li>
                    </ul>
                    <div className="mt-6 flex items-center gap-4">
                        <a
                            href="https://www.instagram.com/hoofsandhidesblr?igsh=MWR4dXRnb2t1anhwZQ%3D%3D&utm_source=qr"
                            className="w-9 h-9 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                            aria-label="Instagram"
                            data-testid="footer-instagram"
                        >
                            <Instagram size={14} />
                        </a>
                        <a
                            href="#"
                            className="w-9 h-9 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                            aria-label="Facebook"
                            data-testid="footer-facebook"
                        >
                            <Facebook size={14} />
                        </a>
                    </div>
                </div>

                <div className="md:col-span-2">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-white/40 mb-4">
                        Wander
                    </p>
                    <ul className="space-y-3 text-sm">
                        {[
                            { to: "/", l: "Home" },
                            { to: "/collection", l: "Collection" },
                            { to: "/about", l: "About" },
                            { to: "/#appointment", l: "Book" },
                        ].map((i) => (
                            <li key={i.to}>
                                <Link
                                    to={i.to}
                                    className="text-white/70 hover:text-white transition-colors hover-underline"
                                    data-testid={`footer-link-${i.l.toLowerCase()}`}
                                >
                                    {i.l}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="px-6 sm:px-10 lg:px-16 py-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <p className="text-xs text-white/40 font-light">
                    © {new Date().getFullYear()} Hoofs and Hides. All rights reserved.
                </p>
                <p className="text-xs text-white/40 font-mono-custom tracking-widest">
                    MADE SLOWLY · IN INDIA
                </p>
            </div>
        </footer>
    );
}
